import React from 'react';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SUPPORTED_LANGS, LANGUAGE_LABELS, MAIN_TOOLS, EXAM_TOOLS, getLocalizedUrl, getLanguageFromPath } from '../../_i18n/urlHelper';

const Footer = (props) => {
    const pathname = (props.location && props.location.pathname) || (typeof window !== 'undefined' ? window.location.pathname : '/');
    const pathLang = getLanguageFromPath(pathname).lang;
    const intlLang = props.intl && props.intl.locale ? props.intl.locale.split('-')[0].toLowerCase() : null;
    const currentLang = (props.currentLang && props.currentLang !== 'en')
        ? props.currentLang
        : (pathLang && pathLang !== 'en')
            ? pathLang
            : (intlLang && SUPPORTED_LANGS.includes(intlLang))
                ? intlLang
                : 'en';
    const langPrefix = currentLang && currentLang !== 'en' ? `/${currentLang}` : '';
    const cleanPath = pathname.split('?')[0].replace(/\/+$/, '') || '/';
    const isHome = cleanPath === '/' || SUPPORTED_LANGS.some(lang => cleanPath === `/${lang}`);

    return (
        <footer className="footer mt-5 py-4 bg-dark text-white border-top border-secondary">
            <div className="container">
                {/* Languages Section - Crucial for Googlebot Crawling & Interlinking */}
                <div className="mb-3">
                    <h6 className="text-uppercase text-muted font-weight-bold mb-2" style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                        <FormattedMessage id="footer.languages" defaultMessage="Languages" />
                    </h6>
                    <div className="d-flex flex-wrap gap-2" style={{ gap: '0.5rem 1rem' }}>
                        {SUPPORTED_LANGS.map(lang => {
                            const isCurrent = lang === currentLang;
                            const url = getLocalizedUrl(pathname, lang);
                            return (
                                <a
                                    key={lang}
                                    href={url}
                                    className={`footer-lang-link ${isCurrent ? 'text-white font-weight-bold' : 'text-muted'}`}
                                    style={{
                                        fontSize: '0.85rem',
                                        textDecoration: isCurrent ? 'underline' : 'none'
                                    }}
                                    title={LANGUAGE_LABELS[lang]}
                                >
                                    {LANGUAGE_LABELS[lang]}
                                </a>
                            );
                        })}
                    </div>
                </div>

                {/* Heavy directory sections rendered ONLY on Home page to prevent thin-content/boilerplate penalty on 27 internal pages */}
                {isHome ? (
                    <React.Fragment>
                        {/* Exam Prep & Certification Timers Section - High RPM Focus */}
                        <div className="mb-3 pt-2 border-top border-secondary">
                            <h6 className="text-uppercase text-success font-weight-bold mb-2" style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                                <FormattedMessage id="footer.examTimers" defaultMessage="🎯 Exam Prep & Certification Timers" />
                            </h6>
                            <div className="d-flex flex-wrap" style={{ gap: '0.4rem 1rem' }}>
                                {EXAM_TOOLS.map(tool => {
                                    const toolUrl = `/${tool.path}${langPrefix}`;
                                    return (
                                        <a
                                            key={tool.path}
                                            href={toolUrl}
                                            className="text-muted"
                                            style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                            title={tool.title}
                                        >
                                            {tool.icon} <FormattedMessage id={tool.key || 'header.exam.all'} defaultMessage={tool.title} />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Classroom & Noise Management Section - High Engagement & RPM */}
                        <div className="mb-3 pt-2 border-top border-secondary">
                            <h6 className="text-uppercase text-warning font-weight-bold mb-2" style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                                🎓 <FormattedMessage id="footer.classroomTimers" defaultMessage="Classroom & Noise Management Timers" />
                            </h6>
                            <div className="d-flex flex-wrap" style={{ gap: '0.4rem 1rem' }}>
                                <a
                                    href={`/pacman-timer${langPrefix}`}
                                    className="text-white font-weight-bold"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🕹️ <FormattedMessage id="header.nav.pacman" defaultMessage="Pac-Man Classroom Timer" />
                                </a>
                                <a
                                    href={`/visual-timer${langPrefix}`}
                                    className="text-white font-weight-bold"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🟢 <FormattedMessage id="header.nav.visualTimer" defaultMessage="Visual Classroom Timer" />
                                </a>
                                <a
                                    href={`/bouncy-balls-noise-meter${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🔴 <FormattedMessage id="header.nav.bouncyBalls" defaultMessage="Bouncy Balls Noise Meter" />
                                </a>
                                <a
                                    href={`/math-bingo${langPrefix}`}
                                    className="text-white font-weight-bold"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🎯 <FormattedMessage id="header.nav.mathBingo" defaultMessage="Math Bingo Game" />
                                </a>
                                <a
                                    href={`/math-wizard${langPrefix}`}
                                    className="text-white font-weight-bold"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🧙‍♂️ <FormattedMessage id="header.nav.mathWizard" defaultMessage="Math Wizard RPG" />
                                </a>
                                <a
                                    href={`/adventure-runner${langPrefix}`}
                                    className="text-white font-weight-bold"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🧭 <FormattedMessage id="header.nav.adventureRunner" defaultMessage="Adventure Runner" />
                                </a>
                                <a
                                    href={`/digital-escape-room${langPrefix}`}
                                    className="text-white font-weight-bold"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🔐 <FormattedMessage id="header.nav.escapeRoom" defaultMessage="Digital Escape Room" />
                                </a>
                                <a
                                    href={`/monster-race${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    👾 <FormattedMessage id="header.nav.monsterRace" defaultMessage="Monster Race" />
                                </a>
                                <a
                                    href={`/rocket-race${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🚀 <FormattedMessage id="header.nav.rocketRace" defaultMessage="Rocket Race" />
                                </a>
                                <a
                                    href={`/running-race${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🏃 <FormattedMessage id="header.nav.runningRace" defaultMessage="Running Race" />
                                </a>
                                <a
                                    href={`/swimming-race${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🏊 <FormattedMessage id="header.nav.swimmingRace" defaultMessage="Swimming Race" />
                                </a>
                                <a
                                    href={`/snail-race${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🐌 <FormattedMessage id="header.nav.snailRace" defaultMessage="Snail Race" />
                                </a>
                                <a
                                    href={`/duck-race${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🦆 <FormattedMessage id="header.nav.duckRace" defaultMessage="Duck Race" />
                                </a>
                                <a
                                    href={`/horse-race${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🐎 <FormattedMessage id="header.nav.horseRace" defaultMessage="Horse Race" />
                                </a>
                                <a
                                    href={`/dynamite-timer${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🧨 <FormattedMessage id="header.nav.dynamite" defaultMessage="Dynamite Timer" />
                                </a>
                                <a
                                    href={`/egg-timer${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🍳 <FormattedMessage id="header.nav.egg" defaultMessage="Egg Timer" />
                                </a>
                                <a
                                    href={`/candle-timer${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🕯️ <FormattedMessage id="header.nav.candle" defaultMessage="Candle Timer" />
                                </a>
                                <a
                                    href={`/bomb-timer${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    💣 <FormattedMessage id="header.nav.bomb" defaultMessage="Bomb Timer" />
                                </a>
                                <a
                                    href={`/rocket-timer${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🚀 <FormattedMessage id="header.nav.rocket" defaultMessage="Rocket Timer" />
                                </a>
                                <a
                                    href={`/cat-timer${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🐱 <FormattedMessage id="header.nav.cat" defaultMessage="Cat Timer" />
                                </a>
                                <a
                                    href={`/christmas-timer${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🎄 <FormattedMessage id="header.nav.christmas" defaultMessage="Christmas Timer" />
                                </a>
                                <a
                                    href={`/countdown-new-year${langPrefix}`}
                                    className="text-muted"
                                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                >
                                    🎆 <FormattedMessage id="header.nav.newYear" defaultMessage="New Year Countdown" />
                                </a>
                            </div>
                        </div>

                        {/* Main Tools Internal Links */}
                        <div className="mb-3 pt-2 border-top border-secondary">
                            <h6 className="text-uppercase text-muted font-weight-bold mb-2" style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                                <FormattedMessage id="footer.tools" defaultMessage="Tools & Timers" />
                            </h6>
                            <div className="d-flex flex-wrap" style={{ gap: '0.4rem 1rem' }}>
                                {MAIN_TOOLS.map(tool => {
                                    const toolUrl = tool.path ? `/${tool.path}${langPrefix}` : (langPrefix || '/');
                                    return (
                                        <a
                                            key={tool.title}
                                            href={toolUrl}
                                            className="text-muted"
                                            style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                                        >
                                            <FormattedMessage id={tool.key || 'header.nav.stopwatch'} defaultMessage={tool.title} />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </React.Fragment>
                ) : (
                    /* Minimal internal navigation bar: clean, high signal-to-noise ratio for search engines */
                    <div className="mb-3 pt-2 border-top border-secondary d-flex flex-wrap align-items-center" style={{ gap: '0.5rem 1.25rem', fontSize: '0.85rem' }}>
                        <a href={langPrefix || '/'} className="text-white font-weight-bold" style={{ textDecoration: 'none' }}>
                            ⏱️ <FormattedMessage id="header.nav.stopwatch" defaultMessage="Stopwatch" />
                        </a>
                        <a href={`/countdown-timer${langPrefix}`} className="text-muted" style={{ textDecoration: 'none' }}>
                            ⏳ <FormattedMessage id="header.nav.countdown" defaultMessage="Countdown Timer" />
                        </a>
                        <a href={`/pomodoro-timer${langPrefix}`} className="text-muted" style={{ textDecoration: 'none' }}>
                            🍅 <FormattedMessage id="header.nav.pomodoro" defaultMessage="Pomodoro Timer" />
                        </a>
                        <a href={`/exam-timer${langPrefix}`} className="text-muted" style={{ textDecoration: 'none' }}>
                            🎓 <FormattedMessage id="header.exam.all" defaultMessage="Exam Prep" />
                        </a>
                    </div>
                )}

                {/* Bottom Legal & Copyright */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center pt-2 border-top border-secondary">
                    <ul className="list-inline mb-2 mb-md-0 no-ad">
                        <li className="list-inline-item">
                            <a href="/terms" rel="nofollow" className="text-muted" style={{ fontSize: '0.8rem' }}>
                                <FormattedMessage id="footer.terms" />
                            </a>
                        </li>
                        <li className="list-inline-item">
                            <a href="/privacy" rel="nofollow" className="text-muted" style={{ fontSize: '0.8rem' }}>
                                <FormattedMessage id="footer.privacy" />
                            </a>
                        </li>
                    </ul>
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                        stopwatch-timer.net © {new Date().getFullYear()}
                    </span>
                </div>
            </div>
        </footer>
    );
};

export default withRouter(injectIntl(Footer));